<template>
  <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
    <div class="rounded-t mb-0 px-4 py-4 border-b border-blueGray-100">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">Subscription</h6>
          <h2 class="text-blueGray-700 text-xl font-semibold">活动进度通知订阅</h2>
        </div>
        <div class="text-right">
          <div class="text-xs text-blueGray-400">当前可用订阅</div>
          <div class="text-3xl leading-none font-semibold text-blueGray-700">{{ availableTotal }}</div>
          <div v-if="updatedAt" class="mt-1 text-xs text-blueGray-400">更新于 {{ updatedAt }}</div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="p-8 text-center text-blueGray-500">正在加载订阅统计…</div>
    <div v-else-if="errorMessage" class="p-8 text-center text-red-500">{{ errorMessage }}</div>
    <div v-else-if="!dashboard" class="p-8 text-center text-blueGray-500">暂无订阅统计数据</div>
    <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 pt-4">
        <div v-for="item in comparisonItems" :key="item.key" class="rounded border border-blueGray-100 p-4">
          <div class="flex items-center justify-between mb-3">
            <span class="font-semibold text-blueGray-700">{{ item.date_label }}</span>
            <span v-if="item.key === 'today'" class="text-xs text-green-600">实时累计</span>
          </div>
          <div v-if="!item.has_stat_period" class="py-5 text-center text-sm text-blueGray-400">未统计</div>
          <dl v-else class="grid grid-cols-2 gap-y-2 text-sm">
            <div><dt class="text-blueGray-400">新增</dt><dd class="font-semibold text-green-600">{{ item.new_count }}</dd></div>
            <div><dt class="text-blueGray-400">已发送</dt><dd class="font-semibold text-blue-600">{{ item.consumed_count }}</dd></div>
            <div><dt class="text-blueGray-400">已取消</dt><dd class="font-semibold text-amber-600">{{ item.cancelled_count }}</dd></div>
            <div><dt class="text-blueGray-400">发送失败</dt><dd class="font-semibold text-red-600">{{ item.failed_count }}</dd></div>
            <div class="col-span-2 flex justify-between border-t border-blueGray-100 pt-2"><dt class="text-blueGray-500">净变化</dt><dd :class="item.net_change >= 0 ? 'text-green-600' : 'text-red-600'" class="font-semibold">{{ formatNetChange(item.net_change) }}</dd></div>
          </dl>
        </div>
      </div>

      <div class="p-4 flex-auto">
        <div v-if="!hasTrendData" class="h-350-px flex items-center justify-center text-blueGray-400">暂无上线后的每日统计数据</div>
        <div v-else class="relative h-350-px"><canvas ref="chartCanvas"></canvas></div>
      </div>
    </template>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
import { luckdrawApi } from "@/api/luckdraw.js";
import { buildTrendDatasets, normalizeComparison } from "./activityProgressSubscriptionPresenter.js";

Chart.register(...registerables);

export default {
  data() {
    return {
      loading: true,
      errorMessage: "",
      dashboard: null,
      chart: null,
    };
  },
  computed: {
    availableTotal() {
      return Number((this.dashboard || {}).available_total || 0).toLocaleString();
    },
    updatedAt() {
      const timestamp = Number((this.dashboard || {}).updated_at || 0);
      return timestamp ? new Date(timestamp * 1000).toLocaleString("zh-CN", { hour12: false }) : "";
    },
    comparisonItems() {
      const comparison = (this.dashboard || {}).comparison || {};
      return [
        { key: "today", ...normalizeComparison(comparison.today) },
        { key: "yesterday", ...normalizeComparison(comparison.yesterday) },
        { key: "seven_days_ago", ...normalizeComparison(comparison.seven_days_ago) },
      ];
    },
    hasTrendData() {
      return ((this.dashboard || {}).items || []).some((item) => item.has_stat_period);
    },
  },
  async mounted() {
    await this.loadDashboard();
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    formatNetChange(value) {
      const number = Number(value || 0);
      return number > 0 ? `+${number}` : number;
    },
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const result = await luckdrawApi.getActivityProgressSubDashboard({ days: 14 });
        if (result.sta !== 0 || !result.data) {
          throw new Error(result.msg || "加载失败");
        }
        this.dashboard = result.data;
      } catch (error) {
        this.errorMessage = error.message || "加载活动进度通知订阅统计失败";
      } finally {
        this.loading = false;
      }
      if (this.dashboard) {
        await this.$nextTick();
        this.renderChart();
      }
    },
    renderChart() {
      this.destroyChart();
      if (!this.hasTrendData || !this.$refs.chartCanvas) {
        return;
      }
      const trend = buildTrendDatasets(this.dashboard.items || []);
      this.chart = new Chart(this.$refs.chartCanvas.getContext("2d"), {
        data: {
          labels: trend.labels,
          datasets: [
            { type: "bar", label: "新增", data: trend.newCount, backgroundColor: "#10b981" },
            { type: "bar", label: "已发送", data: trend.consumedCount, backgroundColor: "#3b82f6" },
            { type: "bar", label: "已取消", data: trend.cancelledCount, backgroundColor: "#f59e0b" },
            { type: "bar", label: "发送失败", data: trend.failedCount, backgroundColor: "#ef4444" },
            { type: "line", label: "净变化", data: trend.netChange, borderColor: "#8b5cf6", backgroundColor: "#8b5cf6", tension: 0.3, spanGaps: false },
          ],
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          plugins: { legend: { position: "bottom" } },
          scales: {
            x: { grid: { display: false } },
            y: { title: { display: true, text: "条" }, ticks: { precision: 0 } },
          },
        },
      });
    },
  },
};
</script>
